import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal
} from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import {
  PageFormIdEnum,
  businessProposalInitStep
} from "src/app/classes/enums";
import { BusinessProposalService } from "src/app/services/business-proposal.service";
import { FormService } from "src/app/services/form.service";
import { IconService } from "src/app/services/icon.service";
import { MappingService } from "src/app/services/mapping.service";
import { PagingService } from "src/app/services/paging.service";
import { UrlService } from "src/app/services/url.service";
import { IconComponent } from "../icon/icon.component";
import { ProgressBarComponent } from "../progress-bar/progress-bar.component";
import { ImageResolverPipe } from "../../../pipes/sharedpipes/image-resolver.pipe";
import {
  BookingState,
  selectAreAllRequiredPackagesSelected,
  selectBookingState
} from "src/app/state";
import { Store } from "@ngrx/store";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  imports: [
    RouterLink,
    IconComponent,
    ProgressBarComponent,
    ImageResolverPipe,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  public PageFormIdEnum = PageFormIdEnum;
  public isBusinessProposalActive = false;
  public businessProposalInitStep = businessProposalInitStep;

  private readonly bookingState: Signal<BookingState>;
  private readonly areAllPackagesSelected: Signal<boolean>;

  constructor(
    public translate: TranslateService,
    public formService: FormService,
    public pagingService: PagingService,
    public urlService: UrlService,
    public mappingService: MappingService,
    public router: Router,
    public iconService: IconService,
    private businessProposalService: BusinessProposalService,
    private store: Store
  ) {
    this.bookingState = this.store.selectSignal(selectBookingState);
    this.areAllPackagesSelected = this.store.selectSignal(
      selectAreAllRequiredPackagesSelected
    );
  }
  ngOnInit(): void {
    this.subscription.add(
      this.businessProposalService.businessProposal$.subscribe({
        next: (proposal) => {
          this.isBusinessProposalActive = Boolean(proposal);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  isCurrentFormValid(step: number): boolean {
    const state = this.bookingState();

    switch (step) {
      case PageFormIdEnum.aboutVehicle:
        return !!state.carInfo && !!state.odometer;

      case PageFormIdEnum.workshopSystemSelection:
        return (
          (!!state.selectedWorkshopSystems &&
            state.selectedWorkshopSystems.length > 0) ||
          state.isBusinessProposalActive
        );

      case PageFormIdEnum.findDealer:
        return !!state.selectedDealerNumber;

      case PageFormIdEnum.chooseSpecifications:
        return this.areAllPackagesSelected();

      case PageFormIdEnum.extraServices:
        return (
          this.formService.extraServicesForm?.valid ||
          (this.areAllPackagesSelected() &&
            this.formService.timeSelectionForm?.valid)
        );

      case PageFormIdEnum.summary:
        const step1Valid = !!state.carInfo;
        const step2Valid =
          !!state.selectedWorkshopSystems &&
          state.selectedWorkshopSystems.length > 0;
        const step3Valid = !!state.selectedDealerNumber;
        const step4Valid = this.areAllPackagesSelected();
        const step5Valid =
          this.formService.extraServicesForm?.valid ||
          (this.areAllPackagesSelected() &&
            this.formService.timeSelectionForm?.valid);
        const step6Valid = this.formService.timeSelectionForm?.valid ?? false;
        const step7Valid =
          this.formService.contactInformationForm?.valid ?? false;

        return (
          step1Valid &&
          step2Valid &&
          step3Valid &&
          step4Valid &&
          step5Valid &&
          step6Valid &&
          step7Valid
        );

      default:
        const control = this.formService.form.get(
          this.mappingService.stepNumberToFormName(step)
        );
        return control ? control.valid : false;
    }
  }

  onNavigate(step: number) {
    const isBusinessProposalIsActiveAndPrevStepOrNextStepInvalid =
      this.businessProposalService.isBusinessProposalActive &&
      (step < 4 ||
        (this.pagingService.formStepLocation > step &&
          !this.formService.form.get(
            this.mappingService.stepNumberToFormName(step > 1 ? step - 1 : 1)
          ).valid));
    const isNextStepInvalid =
      this.pagingService.formStepLocation <= step &&
      !this.formService.form.get(
        this.mappingService.stepNumberToFormName(step > 1 ? step - 1 : 1)
      ).valid;

    if (
      isBusinessProposalIsActiveAndPrevStepOrNextStepInvalid ||
      isNextStepInvalid
    ) {
      return;
    }

    this.router.navigate([this.urlService.getBasePath() + "/form/", step]);
  }
}
